/**
 * Iter 67br — Vendor sub-accounts management UI.
 *
 * Vendor owners + vendor-admin staff can create, edit, deactivate, and
 * delete staff members. Agents/Accountants see the list but the add/edit
 * controls are hidden via aria + the backend's 403 gate.
 *
 * Permission picker (Agent role only) toggles checkboxes that map 1:1 to
 * `VENDOR_STAFF_PERMISSIONS` in the backend. Admin/Accountant roles surface
 * an info banner explaining the role's implicit grants instead of toggles.
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Loader2, UserPlus, Trash2, Pencil, ShieldCheck, ShieldAlert, ShieldOff, BadgeCheck } from "lucide-react";
import { toast } from "sonner";

// Mirrors backend `VENDOR_STAFF_PERMISSIONS`. Order is the order the
// checkboxes render. Labels are user-facing.
const AGENT_PERMISSIONS = [
  { key: "manage_bookings",     label: "Manage bookings",          desc: "Create, edit, cancel, approve" },
  { key: "manage_cars",         label: "Manage vehicles",          desc: "Add, edit, archive cars" },
  { key: "manage_prices",       label: "Manage prices",            desc: "Base rates + date-range rules" },
  { key: "manage_availability", label: "Manage availability",      desc: "Block unavailable ranges" },
  { key: "manage_customers",    label: "Manage customers",         desc: "Edit booking-customer details" },
  { key: "manage_invoices",     label: "Manage invoices",          desc: "Create, send, void invoices" },
  { key: "manage_agreements",   label: "Manage agreements",        desc: "Send & sign rental agreements" },
  { key: "view_finance",        label: "View finance",             desc: "Payouts, balances, P&L (read-only)" },
  { key: "view_payments",       label: "View payments",            desc: "Reservation payment summaries & history" },
  { key: "collect_payments",    label: "Collect payments",         desc: "Record rental balance / partial payments" },
  { key: "collect_deposits",    label: "Collect deposits",         desc: "Record or request security deposits" },
  { key: "edit_payments",       label: "Edit / void payments",     desc: "Correct mistakes — full audit kept" },
  { key: "issue_refunds",       label: "Issue refunds",            desc: "Refunds + deposit release/capture" },
  { key: "view_reports",        label: "View financial reports",   desc: "Vehicle revenue, expenses & profit" },
  { key: "add_expenses",        label: "Add vehicle expenses",     desc: "Record fuel, maintenance, repairs…" },
  { key: "edit_expenses",       label: "Edit / void expenses",     desc: "Corrections — full audit kept" },
  { key: "export_reports",      label: "Export reports",           desc: "Download PDF / CSV financials" },
];

const ROLE_TONES = {
  owner:          { bg: "bg-violet-100",  fg: "text-violet-900",  ring: "ring-violet-200",  label: "Owner",      icon: ShieldCheck },
  admin:          { bg: "bg-emerald-100", fg: "text-emerald-900", ring: "ring-emerald-200", label: "Admin",       icon: ShieldCheck },
  agent:          { bg: "bg-blue-100",    fg: "text-blue-900",    ring: "ring-blue-200",    label: "Agent",       icon: BadgeCheck },
  accountant:     { bg: "bg-amber-100",   fg: "text-amber-900",   ring: "ring-amber-200",   label: "Accountant",  icon: ShieldAlert },
  external_agent: { bg: "bg-pink-100",    fg: "text-pink-900",    ring: "ring-pink-200",    label: "Referral partner", icon: BadgeCheck },
};

function _RoleChip({ role }) {
  const tone = ROLE_TONES[role] || ROLE_TONES.agent;
  const Icon = tone.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${tone.bg} ${tone.fg} ring-1 ${tone.ring} text-[10px] font-bold uppercase tracking-wider`}>
      <Icon className="h-3 w-3" /> {tone.label}
    </span>
  );
}

const EMPTY_DRAFT = { name: "", email: "", password: "", staff_role: "agent", permissions: ["manage_bookings"], referral_percent: 5 };

export default function VendorStaffTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/vendor/staff");
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e) || "Could not load staff");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setDraft({ ...EMPTY_DRAFT });
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setDraft({
      name: row.name || "",
      email: row.email || "",
      password: "",  // empty = keep existing on PATCH
      staff_role: row.staff_role || "agent",
      permissions: Array.isArray(row.permissions) ? row.permissions : [],
      referral_percent: row.referral_percent != null ? Number(row.referral_percent) : 5,
    });
    setOpen(true);
  };

  const togglePerm = (key) => {
    setDraft((d) => {
      const next = new Set(d.permissions);
      if (next.has(key)) next.delete(key); else next.add(key);
      return { ...d, permissions: Array.from(next) };
    });
  };

  const save = async () => {
    if (!draft.name?.trim()) { toast.error("Name is required."); return; }
    if (!editing && !draft.email?.trim()) { toast.error("Email is required."); return; }
    if (!editing && (draft.password || "").length < 8) { toast.error("Password must be at least 8 characters."); return; }
    setSaving(true);
    try {
      if (editing) {
        const body = {
          name: draft.name,
          staff_role: draft.staff_role,
          permissions: draft.permissions,
        };
        if (draft.password) body.password = draft.password;
        if (draft.staff_role === "external_agent" && draft.referral_percent !== "") {
          body.referral_percent = Number(draft.referral_percent);
        }
        await api.patch(`/vendor/staff/${editing.id}`, body);
        toast.success("Staff updated.");
      } else {
        const body = {
          name: draft.name,
          email: draft.email,
          password: draft.password,
          staff_role: draft.staff_role,
          permissions: draft.permissions,
        };
        if (draft.staff_role === "external_agent") {
          body.referral_percent = Number(draft.referral_percent || 0);
        }
        await api.post("/vendor/staff", body);
        toast.success("Staff member added.");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const setActive = async (row, active) => {
    try {
      await api.patch(`/vendor/staff/${row.id}`, { is_active: active });
      toast.success(active ? "Activated" : "Deactivated");
      load();
    } catch (e) {
      toast.error(formatApiError(e) || "Update failed");
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Remove ${row.name}? They will no longer be able to log in.`)) return;
    try {
      await api.delete(`/vendor/staff/${row.id}`);
      toast.success("Staff removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e) || "Delete failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="vendor-staff-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-2xl font-semibold tracking-tight">Staff accounts</h2>
          <p className="text-sm text-neutral-500 mt-1 max-w-xl">
            Give team members their own login. Every action they take is recorded under their name in the activity log.
          </p>
        </div>
        <Button onClick={openCreate} className="rounded-full" data-testid="vendor-staff-add-btn">
          <UserPlus className="h-4 w-4 mr-1.5" /> Add staff member
        </Button>
      </div>

      {/* Iter 67bu — Referral-partner discovery banner. Tells the vendor that
          external_agents log into a dedicated dashboard at /dashboard/partner
          and where to grab their referral link. */}
      <div className="rounded-2xl bg-gradient-to-br from-pink-50/80 to-rose-50/40 ring-1 ring-pink-200 p-4 flex items-start gap-3" data-testid="vendor-staff-partner-banner">
        <div className="h-9 w-9 rounded-xl bg-pink-100 text-pink-700 flex items-center justify-center ring-1 ring-pink-200 shrink-0">
          <BadgeCheck className="h-4 w-4" />
        </div>
        <div className="text-sm text-pink-900 leading-relaxed">
          <strong>Referral partners</strong> (hotels, agencies, concierges) log in at <span className="font-mono bg-white/60 px-1.5 py-0.5 rounded ring-1 ring-pink-200 text-[12px]">/dashboard/partner</span> to copy their unique link + see commissions earned. Click the violet code chip next to a partner row to copy the link straight to your clipboard.
        </div>
      </div>

      {loading ? (
        <div className="rounded-2xl border border-border p-12 flex items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-10 text-center" data-testid="vendor-staff-empty">
          <div className="mx-auto h-12 w-12 rounded-2xl bg-emerald-100 flex items-center justify-center mb-3">
            <UserPlus className="h-5 w-5 text-emerald-700" />
          </div>
          <div className="font-semibold text-neutral-900">No staff yet</div>
          <div className="text-sm text-neutral-500 mt-1">Add your first team member to delegate bookings, invoices, or reception.</div>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-border bg-white" data-testid="vendor-staff-table">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50/60 text-[10px] uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="text-left px-4 py-3 font-bold">Name</th>
                <th className="text-left px-4 py-3 font-bold">Email</th>
                <th className="text-left px-4 py-3 font-bold">Role</th>
                <th className="text-left px-4 py-3 font-bold">Permissions</th>
                <th className="text-left px-4 py-3 font-bold">Status</th>
                <th className="text-right px-4 py-3 font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border" data-testid={`vendor-staff-row-${r.id}`}>
                  <td className="px-4 py-3 font-semibold text-neutral-900">{r.name}</td>
                  <td className="px-4 py-3 text-neutral-700">{r.email}</td>
                  <td className="px-4 py-3"><_RoleChip role={r.staff_role} /></td>
                  <td className="px-4 py-3">
                    {r.staff_role === "agent" ? (
                      <span className="text-xs text-neutral-500">{(r.permissions || []).length} permission{(r.permissions || []).length === 1 ? "" : "s"}</span>
                    ) : r.staff_role === "external_agent" ? (
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-pink-900 bg-pink-50 ring-1 ring-pink-200 px-1.5 py-0.5 rounded">
                          {r.referral_percent}% commission
                        </span>
                        {r.referral_code && (
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-violet-900 bg-violet-50 ring-1 ring-violet-200 px-1.5 py-0.5 rounded hover:bg-violet-100 transition"
                            onClick={() => {
                              const url = `${window.location.origin}/cars?ref=${encodeURIComponent(r.referral_code)}`;
                              navigator.clipboard.writeText(url).then(
                                () => toast.success(`Link copied! ${r.referral_code}`),
                                () => toast.error("Could not copy link"),
                              );
                            }}
                            data-testid={`vendor-staff-copy-link-${r.id}`}
                            title={`Copy /cars?ref=${r.referral_code} to clipboard`}
                          >
                            <span className="font-mono">{r.referral_code}</span>
                          </button>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-neutral-500 italic">Role-based</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {r.is_active ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700">● Active</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-neutral-500">● Deactivated</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(r)} data-testid={`vendor-staff-edit-${r.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => setActive(r, !r.is_active)} data-testid={`vendor-staff-toggle-${r.id}`}>
                        {r.is_active ? <ShieldOff className="h-3.5 w-3.5 text-amber-600" /> : <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(r)} data-testid={`vendor-staff-delete-${r.id}`}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <_StaffDialog
        open={open}
        editing={editing}
        draft={draft}
        setDraft={setDraft}
        togglePerm={togglePerm}
        saving={saving}
        onClose={() => setOpen(false)}
        onSave={save}
      />
    </div>
  );
}

function _StaffDialog({ open, editing, draft, setDraft, togglePerm, saving, onClose, onSave }) {
  const showPermPicker = draft.staff_role === "agent";
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-xl" data-testid="vendor-staff-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit staff member" : "Add staff member"}</DialogTitle>
          <DialogDescription>
            {editing ? "Update the role, permissions, or reset the password." : "They will log in with the email and password you set here."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Name</Label>
              <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="mt-1" data-testid="vendor-staff-name" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" disabled={!!editing} value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} className="mt-1" data-testid="vendor-staff-email" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Role</Label>
              <Select value={draft.staff_role} onValueChange={(v) => setDraft({ ...draft, staff_role: v })}>
                <SelectTrigger className="mt-1" data-testid="vendor-staff-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Vendor Admin · Full access</SelectItem>
                  <SelectItem value="agent">Agent / Employee · Granular</SelectItem>
                  <SelectItem value="accountant">Accountant · Billing + view-only finance</SelectItem>
                  <SelectItem value="external_agent">Referral partner · Hotel / agency commission</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{editing ? "New password (optional)" : "Password"}</Label>
              <Input
                type="password"
                value={draft.password}
                onChange={(e) => setDraft({ ...draft, password: e.target.value })}
                placeholder={editing ? "Leave blank to keep current" : "At least 8 characters"}
                className="mt-1"
                data-testid="vendor-staff-password"
              />
            </div>
          </div>

          {showPermPicker ? (
            <div className="rounded-xl bg-blue-50/40 ring-1 ring-blue-100 p-3">
              <div className="text-xs font-bold uppercase tracking-wider text-blue-900 mb-2">Agent permissions</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {AGENT_PERMISSIONS.map((p) => {
                  const on = (draft.permissions || []).includes(p.key);
                  return (
                    <label key={p.key} className={`flex items-start gap-2 p-2 rounded-lg cursor-pointer transition ${on ? "bg-white ring-1 ring-blue-300" : "hover:bg-white/60"}`} data-testid={`vendor-staff-perm-${p.key}`}>
                      <Switch checked={on} onCheckedChange={() => togglePerm(p.key)} />
                      <div className="min-w-0">
                        <div className="text-xs font-semibold text-neutral-900">{p.label}</div>
                        <div className="text-[10px] text-neutral-500 leading-tight">{p.desc}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          ) : draft.staff_role === "external_agent" ? (
            <div className="rounded-xl bg-pink-50/60 ring-1 ring-pink-200 p-3 space-y-3">
              <div className="text-xs font-bold uppercase tracking-wider text-pink-900">Referral partner</div>
              <div className="text-[11px] text-pink-900/80 leading-snug">
                Hotels, travel agencies, concierges. They earn a commission on every booking that comes through their unique referral link. They do NOT log into the dashboard — give them only the link.
              </div>
              <div>
                <Label className="text-xs">Commission per booking</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    type="number" step="0.5" min="0" max="50"
                    value={draft.referral_percent ?? ""}
                    onChange={(e) => setDraft({ ...draft, referral_percent: e.target.value === "" ? "" : Number(e.target.value) })}
                    className="w-28 tabular-nums"
                    data-testid="vendor-staff-referral-percent"
                  />
                  <span className="text-sm font-semibold text-pink-900">% of rental total</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="rounded-xl bg-emerald-50/50 ring-1 ring-emerald-100 p-3 text-xs text-emerald-900">
              {draft.staff_role === "admin" && "Admins hold every permission — same as the owner, except they cannot manage other admins."}
              {draft.staff_role === "accountant" && "Accountants can view finance (payouts, balances, P&L) and create/edit invoices. Other modules are read-only."}
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={onSave} disabled={saving} data-testid="vendor-staff-save-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            {editing ? "Save changes" : "Add staff"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
